// components/list/listSlashCommands.ts
//
// The curated "/" menu for the List composer.
//
// Copied in shape from components/source/sourceSlashCommands.ts rather than shared with it: the two
// lists answer different questions ("what do I ask a sourcing agent" and "what do I ask of a pile
// of images"), and a single list that grows for both ends up showing a leasing manager a command
// about variations and a marketing manager a command about shortlists.
//
// One difference from Source, and it is deliberate. A Source command is a NAME the agent
// interprets: picking it types "/refine " and the procedure lives in the agent's knowledge base.
// `factory` is not that. It fans out N generations and spends N times, so it is dispatched by the
// composer itself, deterministically, and never handed to a model to interpret. The spend has to be
// exact, not inferred.
export interface ListSlashCommand {
  /** What the user types after "/". */
  name: string;
  title?: string;
  hint: string;
  /**
   * Which half of the menu this belongs to, read by the composer that renders both lists. Absent
   * means our own curated half, the same convention the Source menu uses.
   */
  group?: 'source' | 'agent' | 'list';
}

export const LIST_FACTORY_COMMAND = 'factory';
export const LIST_ARCHETYPE_COMMAND = 'archetype';

/**
 * One entry, on purpose. A menu pre-filled with commands that do not exist yet teaches the user
 * that this menu lies.
 */
export const LIST_SLASH_COMMANDS: ListSlashCommand[] = [
  {
    name: LIST_FACTORY_COMMAND,
    group: 'list',
    title: 'A batch of variations',
    hint: 'Produce several variations from the images you dropped',
  },
  {
    name: LIST_ARCHETYPE_COMMAND,
    group: 'list',
    title: 'Factory archetype',
    hint: 'Name an archetype and attach reference images; direction is optional',
  },
];

/**
 * The menu, matched the same way Source matches its own: trimStart and not trim, because the
 * trailing space is the user saying they have finished naming the command. Trimming it keeps the
 * menu open on "/factory ", and while the menu is open Enter picks instead of sending.
 */
export const matchListSlashCommands = (text: string): ListSlashCommand[] | null => {
  const match = /^\/([a-zA-Z_-]*)$/.exec(text.trimStart());
  if (!match) return null;
  const fragment = (match[1] ?? '').toLowerCase();
  const hits = LIST_SLASH_COMMANDS.filter((command) => command.name.toLowerCase().startsWith(fragment));
  return hits.length > 0 ? hits : null;
};



export type FactoryArchetypeCommandRequest = { name: string; description: string; handle?: string };

/**
 * A short unquoted name keeps the command quick; quotes make an intentional multi-word name
 * unambiguous: `/archetype: "Playful errand"`. An optional description guides the image-derived concept.
 */
export const parseFactoryArchetypeCommand = (text: string): FactoryArchetypeCommandRequest | null => {
  const edit = /^\/archetype\s+edit\s*:\s*@([a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?)\s+([\s\S]+)$/i.exec(text.trim());
  if (edit?.[1] && edit[2]) return { name: '', handle: edit[1].toLowerCase(), description: edit[2].replace(/\s+/g, ' ').trim() };
  const match = /^\/archetype\s*:\s*(?:"([^"\n]{1,120})"|([^\s"\n]{1,120}))(?:\s+([\s\S]*?))?\s*$/i.exec(text.trim());
  if (!match) return null;
  const name = (match[1] ?? match[2] ?? '').trim();
  const description = (match[3] ?? '').replace(/\s+/g, ' ').trim();
  return name ? { name, description } : null;
};

export const isFactoryArchetypeCommandText = (text: string): boolean => /^\/archetype\b/i.test(text.trim());

/** Default when the user names no number: enough to choose from, cheap enough to try. */
export const DEFAULT_FACTORY_VARIATIONS = 4;
/** A hand-typed number that would empty a pool by accident is refused rather than obeyed. */
export const MAX_FACTORY_VARIATIONS = 10;

export interface FactoryGenerateCommandRequest {
  kind: 'generate';
  /**
   * How many variations IN TOTAL, which is what the number in the command means.
   *
   * It briefly meant "per image", so "/factory 3" on three images launched nine generations and
   * spent nine times. A number a user typed is the number of things they expect to get back; the
   * dropped images are the material, not a multiplier.
   */
  variations: number;
  /** Whatever the user wrote after the command, their own instruction, kept verbatim. */
  instruction: string;
  /**
   * The ratio the user named, as width divided by height, or null when they named none.
   *
   * Null means "follow the reference image", which is the useful default: a variation of a portrait
   * photograph that comes back letterboxed in a wide frame is not a variation of it. A ratio typed
   * in the command overrides that, because the user asking for 9:16 knows something the image does
   * not.
   */
  ratio: number | null;
  /** The ratio as the user wrote it, for the line that says what is being produced. */
  ratioLabel: string | null;
  /** All requested format labels. Multiple labels multiply the variation count. */
  ratioLabels: string[];
}

export interface FactoryEditCommandRequest {
  kind: 'edit';
  instruction: string;
}

export type FactoryCommandRequest = FactoryGenerateCommandRequest | FactoryEditCommandRequest;

/**
 * "9:16" or "4x5" anywhere in the instruction, whichever comes first, with the little word that
 * introduces it. Taking "in" along with "9:16" is what stops "in, keep the red socks" being left
 * in the prompt.
 */
const RATIO_PATTERN = /(?:\b(?:in|en|au format|formats?|ratio|aspect)\s+)?\b(\d{1,2})\s*[:x/]\s*(\d{1,2})\b/gi;

const readRatios = (text: string): { ratio: number | null; ratioLabel: string | null; ratioLabels: string[]; rest: string } => {
  const ratioLabels: string[] = [];
  let rest = text;
  for (const match of text.matchAll(RATIO_PATTERN)) {
    const width = Number(match[1]);
    const height = Number(match[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) continue;
    ratioLabels.push(`${width}:${height}`);
  }
  if (ratioLabels.length > 0) {
    rest = text
      .replace(RATIO_PATTERN, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+([,.;:])/g, '$1')
      .replace(/\b(?:\d+\s*)?formats?\b/gi, ' ')
      .replace(/[()&]/g, ' ')
      .replace(/\b(in|en|au format|formats?|ratio|aspect)\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^[,.;:]\s*/, '')
      .replace(/[\s,.;:]+$/g, '')
      .trim();
  }
  return {
    ratio: ratioLabels[0] ? Number(ratioLabels[0].split(':')[0]) / Number(ratioLabels[0].split(':')[1]) : null,
    ratioLabel: ratioLabels[0] ?? null,
    ratioLabels,
    rest,
  };
};

/**
 * Read "/factory", "/factory 6", "/factory 6 variations, keep the background" or
 * "/factory keep the background" into a request, or null when the line is not this command.
 *
 * The count is only taken from the FIRST word after the command, so "make 6 people smile" stays an
 * instruction and does not silently become six variations.
 */
export const parseFactoryCommand = (text: string): FactoryCommandRequest | null => {
  const match = /^\/factory(?:\s+([\s\S]*))?$/i.exec(text.trim());
  if (!match) return null;
  const rest = (match[1] ?? '').trim();
  const edit = /^edit\s*:\s*([\s\S]*)$/i.exec(rest);
  if (edit) return { kind: 'edit', instruction: (edit[1] ?? '').trim() };
  // The count first, then the ratio out of what is left. Order matters: "/factory 3 in 9:16" must
  // read three variations at 9:16, not three-in-nine.
  const leadingNumber = /^(\d{1,2})\s*(?:variations?|x)?(?![:x/]\s*\d)\b[,;:.]?\s*([\s\S]*)$/i.exec(rest);
  if (!leadingNumber) {
    const parsed = readRatios(rest);
    return {
      kind: 'generate',
      variations: DEFAULT_FACTORY_VARIATIONS,
      instruction: parsed.rest,
      ratio: parsed.ratio,
      ratioLabel: parsed.ratioLabel,
      ratioLabels: parsed.ratioLabels,
    };
  }
  const asked = Number(leadingNumber[1]);
  const variations = Number.isFinite(asked) && asked > 0
    ? Math.min(asked, MAX_FACTORY_VARIATIONS)
    : DEFAULT_FACTORY_VARIATIONS;
  const parsed = readRatios((leadingNumber[2] ?? '').trim());
  return {
    kind: 'generate',
    variations,
    instruction: parsed.rest,
    ratio: parsed.ratio,
    ratioLabel: parsed.ratioLabel,
    ratioLabels: parsed.ratioLabels,
  };
};

/** Is this line the factory command? Cheap guard for the send path. */
export const isFactoryCommandText = (text: string): boolean => parseFactoryCommand(text) !== null;
